import React, { useRef, useState } from "react";

import { Button, Chip, Popover, Stack, TextField, Typography } from "@mui/material";
import { SelectAutocomplete } from "components/forms";
import { benchmarkFilters } from "constants/benchmark";
import { useDevice } from "hooks";
import { ReportFilters } from "store/types";

interface StaticFiltersProps {
  filters: ReportFilters;
  filterValues: ReportFilters;
  setFilters: (filters: ReportFilters) => void;
}

const filterOptions: { [key in benchmarkFilters.SUB_ASSET | benchmarkFilters.VINTAGE]: string } = {
  [benchmarkFilters.SUB_ASSET]: "Asset Class",
  [benchmarkFilters.VINTAGE]: "Vintage"
};

export const StaticFilters: React.FC<StaticFiltersProps> = ({ filters, filterValues, setFilters }) => {
  const [selectedValues, setSelectedValues] = useState<ReportFilters[keyof ReportFilters]>([]);
  const [filterEdit, setFilterEdit] = useState<string>("");
  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);
  const anchorRef = useRef<HTMLElement>();
  const { isMobile } = useDevice();

  const handleClose = () => {
    setFilterEdit("");
    setSelectedValues([]);
  };

  const openFilter = (e: React.MouseEvent, filter: string) => {
    anchorRef.current = e.target as HTMLElement;
    setFilterEdit(filter);
    setSelectedValues(filters[filter]);
  };

  const handleSet = () => {
    const copy = { ...filters };
    selectedValues ? (copy[filterEdit] = selectedValues) : delete copy[filterEdit];
    setFilters(copy);
    handleClose();
  };

  const removeFilter = (filter: string): void => {
    const filtersCopy = { ...filters };
    filtersCopy[filter] = [];
    setFilters(filtersCopy);
  };
  return (
    <>
      <Stack sx={{ flexDirection: "row", justifyContent: isMobile ? "flex-start" : "space-between", gap: "10px" }}>
        <Typography sx={{ fontSize: "16px", color: "#0A3142", fontWeight: 500 }}>Filters</Typography>
        {Object.entries(filters).map(([filter, values]) => (
          <Chip
            key={filter}
            sx={{ height: "26px", fontSize: "15px", color: "#0a3142", backgroundColor: "#f5f6f7" }}
            label={`${filterOptions[filter as keyof typeof filterOptions]} (${values.length})`}
            onDelete={() => removeFilter(filter)}
            onClick={(e) => openFilter(e, filter)}
          />
        ))}
      </Stack>
      <Popover
        open={!!filterEdit}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        onClose={handleClose}
        disableEnforceFocus
        // disableAutoFocus
      >
        <Stack sx={{ alignItems: "stretch", p: "30px", gap: "30px", maxWidth: "500px" }}>
          <Stack
            sx={{ borderBottom: "1px solid #dae0e2", pb: "5px" }}
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={{ fontSize: "15px" }}>{filterOptions[filterEdit as keyof typeof filterOptions]}</Typography>
            <Stack flexDirection="row">
              <Button sx={{ color: "#26A6AE" }} variant="text" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                disabled={Boolean(!selectedValues.length)}
                sx={{ color: "#26A6AE", ":disabled": { backgroundColor: "#FFF", color: "#bac7ce" } }}
                variant="text"
                onClick={handleSet}
              >
                Save
              </Button>
            </Stack>
          </Stack>
          <SelectAutocomplete<string, true>
            multiple
            disableCloseOnSelect
            selectAll
            limitTags={1}
            openOnFocus
            options={(filterValues?.[filterEdit] || []).map(String)}
            renderInput={(params) => <TextField {...params} label="Filters" inputRef={inputRef} />}
            sx={{ maxWidth: "300px", minWidth: "300px" }}
            defaultValue={(filters[filterEdit] as Array<string>) || []}
            onChange={(value) => setSelectedValues(value)}
          />
        </Stack>
      </Popover>
    </>
  );
};
